import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router';
import { useExperience, useMarkNotificationsRead, useNotifications, useWorkspaceQueue } from '@/api/hooks';
import { signOut, type Me } from '@/api/session';
import type { WireNotification } from '@/api/wire';
import { ageOf } from '@/notices/age';
import { notificationGist } from '@/notices/explain';
import { Icon } from '@/ui/icons';
import { Avatar, HEADER_ACTION, HeaderButton, IconButton } from '@/ui/primitives';
import { OPEN_PALETTE } from './CommandPalette';
import { useFrame } from './frame';
import { PageHelpButton } from './PageHelp';
import { homeOf } from './nav';
import { useScope } from './scope';
import { useTheme } from './useTheme';
import { Menu, MenuContent, MenuItem } from '@/ui/menu';
import { companyOrigin,isCompanyRecordPath } from './company-navigation';
import { ManualAdd } from '@/directory/ManualAdd';

/**
 * The header every page shares (P:80-168): the way back, what this page is,
 * and what a working day needs at hand — add, find a time, the calendar,
 * what happened, you, and your twin. The page names itself through
 * `<PageHeader>`; the header draws it.
 */
export function TopBar({ me }: { me: Me }) {
  const frame = useFrame();
  const location = useLocation();
  const { scope } = useScope(me);
  // Back goes to the scope's own home: Today in a department, the Workspace in the Control room.
  const home = homeOf(scope);
  const onHome = location.pathname === home || location.pathname === '/';
  const head = frame.head;
  const origin=companyOrigin((location.state as {companyReturn?:unknown}|null)?.companyReturn);
  const companyBack=isCompanyRecordPath(location.pathname)&&origin&&(me.isSuperadmin||me.departments.includes(origin.scopeDepartment))?{to:origin.to,label:origin.label}:null;
  const back = companyBack ?? (head?.back === null ? null : (head?.back ?? (onHome ? null : { to: home, label: scope.kind === 'control' ? 'Workspace' : 'Today' })));
  const twin = frame.twin;
  const { phone, wide, roomy } = frame.layout;
  const showMenu = phone || frame.railHidden;

  return (
    <header className="sticky top-0 z-20 flex min-h-[68px] flex-wrap items-center gap-x-3 gap-y-2.5 border-b border-line bg-page px-6 py-3 phone:px-4">
      {showMenu && (
        <IconButton
          label="Show menu"
          aria-expanded={phone ? frame.drawerOpen : undefined}
          onClick={() => (phone ? frame.setDrawerOpen(true) : frame.setRailHidden(false))}
          glyph="menu"
        />
      )}
      {back && (
        <Link to={back.to} aria-label={`Back to ${back.label}`} className={`${HEADER_ACTION} tracking-label no-underline`}>
          <Icon glyph="back" />
          {roomy && <span>{back.label}</span>}
        </Link>
      )}
      <div className="flex min-w-0 flex-[1_1_210px] flex-col gap-0.5">
        <span className="text-micro uppercase tracking-label text-meta">{head?.kicker ?? ' '}</span>
        <h1 className="m-0 text-readback font-semibold leading-[1.2] [text-wrap:balance]">{head?.title ?? ' '}</h1>
      </div>
      <div className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center gap-1.5">
        {me.isSuperadmin && <BlockedTasks />}
        {/* What agents may use on this page (S:22): read by a lead or a
            superadmin, as the gateway decides it; nobody else is offered it. */}
        {/* A department's page's tools; the Control room holds no department's page. */}
        {/* The tools and grants behind a page are technical: the superadmin's (ADR-0140). */}
        {scope.kind === 'department' && me.isSuperadmin && (
          <HeaderButton
            label="Show which tools and permissions this page has"
            glyph="safety"
            word={wide && 'Permissions'}
            aria-pressed={frame.permissionsOpen}
            className={frame.permissionsOpen ? 'border-strong bg-ink text-page hover:text-page' : ''}
            onClick={() => frame.setPermissionsOpen(!frame.permissionsOpen)}
          />
        )}
        {/* When the column is tight the cluster drops to the calendar alone,
            so the title keeps its room (P:2262-2269). */}
        <ManualAdd wide={wide && roomy} />
        {roomy && (
          <HeaderButton label="Find a time" glyph="findTime" word={wide && 'Find a time'} onClick={() => frame.setCalendar(frame.calendar === 'find' ? 'closed' : 'find')} />
        )}
        {/* On a phone the two do not fit beside the title; one menu holds them. */}
        {(phone || !roomy) && (
          <Menu.Root modal={false}>
            <Menu.Trigger asChild>
              <IconButton label="More" glyph="add" className="data-[state=open]:border-strong data-[state=open]:text-ink" />
            </Menu.Trigger>
            <MenuContent className="w-[min(280px,calc(100vw-32px))]" aria-label="More">
              <MenuItem last={roomy} onSelect={() => window.dispatchEvent(new Event(OPEN_PALETTE))}>
                <Icon glyph="search" />
                Go to, or ask
              </MenuItem>
              {!roomy && (
                <MenuItem last onSelect={() => frame.setCalendar(frame.calendar === 'find' ? 'closed' : 'find')}>
                  <Icon glyph="findTime" />
                  Find a time
                </MenuItem>
              )}
            </MenuContent>
          </Menu.Root>
        )}
        <HeaderButton
          label="Open the calendar"
          glyph="calendar"
          word={wide && 'Calendar'}
          aria-pressed={frame.calendar === 'week'}
          className={frame.calendar === 'week' ? 'border-strong text-ink' : ''}
          onClick={() => frame.setCalendar(frame.calendar === 'week' ? 'closed' : 'week')}
        />
        <PageHelpButton wide={wide} onAsk={(q) => frame.ask(q)} />
        <Bell />
        <PersonMenu me={me} />
        {!frame.chatOpen && !(phone && location.pathname === '/today' && frame.workspaceContext) && (
          <button
            type="button"
            onClick={() => frame.ask()}
            className="min-h-tap whitespace-nowrap bg-[var(--btn-bg)] px-3.5 text-micro uppercase tracking-label text-[var(--btn-fg)] hover:bg-[var(--btn-bg-hover)]"
            aria-label={twin ? `Ask ${twin}` : 'Ask your twin'}
          >
            {twin ?? 'Chat'}
          </button>
        )}
      </div>
    </header>
  );
}

/**
 * A twin's task waiting on a tool is waiting on a superadmin, so it is said
 * where a superadmin looks on every page, not only on /workspace
 * (Amendment 0011). The queue is the Workspace's own, read once for both.
 */
function BlockedTasks() {
  const queue = useWorkspaceQueue(true);
  const blocked = (queue.data?.items ?? []).filter((i) => i.kind === 'task_tool');
  // One task waiting on two tools is one task blocked.
  const tasks = [...new Set(blocked.map((i) => i.href))];
  if (tasks.length === 0) return null;
  const label = tasks.length === 1 ? "1 twin's task blocked" : `${tasks.length} twins' tasks blocked`;
  return (
    <Link
      to={tasks[0]!}
      state={{companyReturn:null}}
      data-testid="tasks-blocked"
      title="A twin's task waits on a tool only a superadmin can grant. Grant it or refuse it on the Workspace."
      className="inline-flex min-h-tap items-center gap-2 border border-warning bg-warning px-3 text-micro uppercase tracking-[0.12em] whitespace-nowrap text-[var(--warning-fg)] no-underline hover:opacity-90"
    >
      {label}
    </Link>
  );
}

function Bell() {
  const navigate = useNavigate();
  const notices = useNotifications();
  const mark = useMarkNotificationsRead();
  const unread = notices.data?.unread ?? 0;
  const items = (notices.data?.items ?? []).slice(0, 8);
  const open = (n: WireNotification) => {
    if (!n.readAt) mark.mutate({ ids: [n.id] });
    void navigate(n.deepLink,{state:{companyReturn:null}});
  };
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>
        <IconButton
          label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          glyph="bell"
          className="data-[state=open]:border-strong data-[state=open]:text-ink"
        >
          {unread > 0 && (
            <span data-testid="unread" className="absolute right-[5px] top-[5px] flex h-4 min-w-4 items-center justify-center bg-warning px-1 text-micro font-bold text-[var(--warning-fg)]">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </IconButton>
      </Menu.Trigger>
      <MenuContent className="max-h-[70dvh] w-[min(400px,calc(100vw-32px))]" aria-label="Notifications">
          <div className="flex min-h-tap items-center gap-2.5 border-b border-line px-4">
            <span className="text-micro uppercase tracking-label text-meta">
              {unread > 0 ? `${unread} unread` : 'Nothing unread'}
            </span>
            {unread > 0 && (
              <Menu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  mark.mutate({ all: true });
                }}
                className="ml-auto flex min-h-tap cursor-pointer items-center px-1 text-micro uppercase tracking-[0.12em] text-secondary underline underline-offset-[3px] outline-none hover:text-ink data-[highlighted]:text-ink"
              >
                Mark all read
              </Menu.Item>
            )}
          </div>
          <div className="flex flex-col overflow-y-auto">
            {items.length === 0 && (
              <p className="m-0 px-4 py-5 text-dense text-meta">Nothing has happened that you need to know about.</p>
            )}
            {items.map((n) => (
              <Menu.Item
                key={n.id}
                onSelect={() => open(n)}
                className={`flex min-h-tap cursor-pointer items-start gap-3 border-b border-line px-4 py-3 text-left outline-none data-[highlighted]:bg-sunken ${n.readAt ? '' : 'bg-raised'}`}
              >
                <Avatar name={n.actor} size={26} agent={n.actor.endsWith('.twin') || /\.agent@/.test(n.actor) ? { label: n.actor } : undefined} />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-dense leading-[1.45]">{n.text}</span>
                  {/* What to do about it, or that nothing is needed: the full account is on the Notifications page. */}
                  <span className="text-label text-meta">
                    {ageOf(n.createdAt)} · <span className={notificationGist(n).needsYou ? 'font-medium text-ink' : ''}>{notificationGist(n).line}</span>
                  </span>
                </span>
                {!n.readAt && <span aria-hidden className="ml-auto mt-2 block h-[7px] w-[7px] shrink-0 bg-warning" />}
              </Menu.Item>
            ))}
          </div>
          <Menu.Item
            onSelect={() => void navigate('/notifications')}
            className="flex min-h-tap cursor-pointer items-center border-t border-line px-4 text-micro uppercase tracking-label text-secondary outline-none data-[highlighted]:bg-raised data-[highlighted]:text-ink"
          >
            Everything that happened →
          </Menu.Item>
      </MenuContent>
    </Menu.Root>
  );
}

function PersonMenu({ me }: { me: Me }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { theme, choose } = useTheme();
  const prefs = useExperience().data?.resolved.preferences ?? {};
  const hours = `${typeof prefs['dayStart'] === 'string' ? prefs['dayStart'] : '07:00'} – ${typeof prefs['dayEnd'] === 'string' ? prefs['dayEnd'] : '19:00'}`;
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>
        <button
          type="button"
          aria-label={`You: ${me.displayName}`}
          className="flex min-h-tap items-center gap-2 border border-control pl-[5px] pr-2 hover:border-strong data-[state=open]:border-strong"
        >
          <Avatar name={me.displayName} size={30} />
          <span aria-hidden className="text-meta"><Icon glyph="chevron" size={12} /></span>
        </button>
      </Menu.Trigger>
      <MenuContent className="w-[min(280px,calc(100vw-32px))]" aria-label="You">
          <div className="flex flex-col gap-0.5 border-b border-line px-4 py-3.5">
            <span className="text-dense font-semibold">{me.displayName}</span>
            <span className="text-dense text-meta">{me.email}</span>
          </div>
          <MenuItem onSelect={() => void navigate('/settings')}>
            Put it back
          </MenuItem>
          <MenuItem
            onSelect={(e) => {
              e.preventDefault();
              choose(theme === 'light' ? 'dark' : 'light');
            }}
          >
            <span>Appearance</span>
            <span className="ml-auto text-label uppercase tracking-[0.12em] text-meta">{theme === 'light' ? 'Light' : 'Dark'}</span>
          </MenuItem>
          <MenuItem onSelect={() => void navigate('/settings')}>
            <span>Day starts and ends</span>
            <span className="ml-auto text-label uppercase tracking-[0.12em] text-meta">{hours}</span>
          </MenuItem>
          <MenuItem onSelect={() => void navigate('/settings/availability')}>
            When I can be booked
          </MenuItem>
          <MenuItem
            last
            onSelect={() => {
              void signOut().then(() => {
                qc.clear();
                void navigate('/signin');
              });
            }}
          >
            Sign out
          </MenuItem>
      </MenuContent>
    </Menu.Root>
  );
}
